/**
 * CohortAnalysis Component
 * 
 * Comprehensive cohort analysis section combining:
 * - CohortRetentionTable
 * - RetentionCurveChart
 * - Average retention metrics
 * - Best/worst cohort highlights
 * - Churn prediction indicators
 * - Subscription tier filter
 * - CSV export functionality
 * 
 * Requirements: 4.1, 4.13, 4.14, 4.15, 4.16, 4.17, 4.18, 4.19, 4.20, 4.21, 4.22, 4.23
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import CohortRetentionTable from '../charts/CohortRetentionTable';
import RetentionCurveChart from '../charts/RetentionCurveChart';
import ExportButton from './ExportButton';
import './CohortAnalysis.css';

const CohortAnalysis = ({
  cohorts = [],
  averageRetention = null,
  bestCohort = null,
  worstCohort = null,
  churnWarnings = [],
  loading = false,
  groupBy = 'month',
  tier = 'all',
  onGroupByChange = null,
  onTierChange = null
}) => {
  const [selectedCohort, setSelectedCohort] = useState(null);

  /**
   * Handle cell click in retention table
   * @param {string} cohortName - Cohort name
   * @param {string} period - Time period
   * @param {number} rate - Retention rate
   */
  const handleCellClick = (cohortName, period, rate) => {
    setSelectedCohort({ cohortName, period, rate });
    // TODO: Show detailed user list modal
    console.log(`Clicked: ${cohortName} - ${period} - ${rate}%`);
  };

  /**
   * Format retention rate for display
   * @param {number|null} rate - Retention rate
   * @returns {string} Formatted rate
   */
  const formatRate = (rate) => {
    if (rate === null || rate === undefined) {
      return 'N/A';
    }
    return `${rate.toFixed(1)}%`;
  };

  /**
   * Export cohort data to CSV
   * @returns {string} CSV content
   */
  const exportToCSV = () => {
    if (!cohorts || cohorts.length === 0) {
      return '';
    }

    // CSV headers
    const headers = ['Cohort', 'Size', 'Week 1', 'Week 2', 'Week 4', 'Month 2', 'Month 3', 'Month 6'];
    
    // CSV rows
    const rows = cohorts.map(cohort => [
      cohort.name,
      cohort.size,
      formatRate(cohort.retention.week1),
      formatRate(cohort.retention.week2),
      formatRate(cohort.retention.week4),
      formatRate(cohort.retention.month2),
      formatRate(cohort.retention.month3),
      formatRate(cohort.retention.month6)
    ]);

    // Add average retention row
    if (averageRetention) {
      rows.push([
        'Average',
        '',
        formatRate(averageRetention.week1),
        formatRate(averageRetention.week2),
        formatRate(averageRetention.week4),
        formatRate(averageRetention.month2),
        formatRate(averageRetention.month3),
        formatRate(averageRetention.month6)
      ]);
    }

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    return csvContent;
  };

  return (
    <div className="cohort-analysis">
      {/* Header with filters and export */}
      <div className="cohort-analysis__header">
        <div className="cohort-analysis__title-section">
          <h2 className="cohort-analysis__title">Cohort Analysis</h2>
          <p className="cohort-analysis__subtitle">
            Track user retention patterns by signup cohort
          </p>
        </div>

        <div className="cohort-analysis__controls">
          {/* Group By Filter */}
          {onGroupByChange && (
            <div className="cohort-analysis__filter">
              <label htmlFor="groupBy" className="cohort-analysis__filter-label">
                Group By:
              </label>
              <select
                id="groupBy"
                value={groupBy}
                onChange={(e) => onGroupByChange(e.target.value)}
                className="cohort-analysis__filter-select"
                disabled={loading}
              >
                <option value="month">Month</option>
                <option value="week">Week</option>
              </select>
            </div>
          )}

          {/* Tier Filter */}
          {onTierChange && (
            <div className="cohort-analysis__filter">
              <label htmlFor="tier" className="cohort-analysis__filter-label">
                Tier:
              </label>
              <select
                id="tier"
                value={tier}
                onChange={(e) => onTierChange(e.target.value)}
                className="cohort-analysis__filter-select"
                disabled={loading}
              >
                <option value="all">All</option>
                <option value="free">Free</option>
                <option value="premium">Premium</option>
                <option value="family">Family</option>
              </select>
            </div>
          )}

          {/* Export Button */}
          <ExportButton
            data={exportToCSV()}
            filename={`cohort-analysis-${groupBy}-${tier}-${new Date().toISOString().split('T')[0]}.csv`}
            disabled={loading || !cohorts || cohorts.length === 0}
          />
        </div>
      </div>

      {/* Summary Metrics */}
      {!loading && cohorts.length > 0 && (
        <div className="cohort-analysis__metrics">
          {/* Average Retention */}
          {averageRetention && (
            <div className="cohort-analysis__metric-card">
              <h3 className="cohort-analysis__metric-title">Average Retention</h3>
              <div className="cohort-analysis__metric-grid">
                <div className="cohort-analysis__metric-item">
                  <span className="cohort-analysis__metric-label">Week 1:</span>
                  <span className="cohort-analysis__metric-value">{formatRate(averageRetention.week1)}</span>
                </div>
                <div className="cohort-analysis__metric-item">
                  <span className="cohort-analysis__metric-label">Week 2:</span>
                  <span className="cohort-analysis__metric-value">{formatRate(averageRetention.week2)}</span>
                </div>
                <div className="cohort-analysis__metric-item">
                  <span className="cohort-analysis__metric-label">Week 4:</span>
                  <span className="cohort-analysis__metric-value">{formatRate(averageRetention.week4)}</span>
                </div>
                <div className="cohort-analysis__metric-item">
                  <span className="cohort-analysis__metric-label">Month 2:</span>
                  <span className="cohort-analysis__metric-value">{formatRate(averageRetention.month2)}</span>
                </div>
                <div className="cohort-analysis__metric-item">
                  <span className="cohort-analysis__metric-label">Month 3:</span>
                  <span className="cohort-analysis__metric-value">{formatRate(averageRetention.month3)}</span>
                </div>
                <div className="cohort-analysis__metric-item">
                  <span className="cohort-analysis__metric-label">Month 6:</span>
                  <span className="cohort-analysis__metric-value">{formatRate(averageRetention.month6)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Best Cohort */}
          {bestCohort && (
            <div className="cohort-analysis__metric-card cohort-analysis__metric-card--success">
              <h3 className="cohort-analysis__metric-title">Best Performing Cohort</h3>
              <div className="cohort-analysis__metric-highlight">
                <span className="cohort-analysis__metric-highlight-name">{bestCohort.name}</span>
                <span className="cohort-analysis__metric-highlight-value">
                  {formatRate(bestCohort.month3Retention)} at Month 3
                </span>
              </div>
            </div>
          )}

          {/* Worst Cohort */}
          {worstCohort && (
            <div className="cohort-analysis__metric-card cohort-analysis__metric-card--warning">
              <h3 className="cohort-analysis__metric-title">Needs Attention</h3>
              <div className="cohort-analysis__metric-highlight">
                <span className="cohort-analysis__metric-highlight-name">{worstCohort.name}</span>
                <span className="cohort-analysis__metric-highlight-value">
                  {formatRate(worstCohort.month3Retention)} at Month 3
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Churn Warnings */}
      {!loading && churnWarnings.length > 0 && (
        <div className="cohort-analysis__churn-warnings">
          <div className="cohort-analysis__churn-icon">⚠️</div>
          <div className="cohort-analysis__churn-content">
            <h3 className="cohort-analysis__churn-title">Churn Risk Alert</h3>
            <p className="cohort-analysis__churn-text">
              The following cohorts show significant retention drops (&gt;50% between periods):
            </p>
            <div className="cohort-analysis__churn-list">
              {churnWarnings.map((cohortName, index) => (
                <span key={index} className="cohort-analysis__churn-badge">
                  {cohortName}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Retention Table */}
      <div className="cohort-analysis__section">
        <h3 className="cohort-analysis__section-title">Retention Table</h3>
        <CohortRetentionTable
          cohorts={cohorts}
          loading={loading}
          onCellClick={handleCellClick}
          groupBy={groupBy}
        />
      </div>

      {/* Retention Curves */}
      <div className="cohort-analysis__section">
        <RetentionCurveChart
          cohorts={cohorts}
          loading={loading}
          groupBy={groupBy}
        />
      </div>

      {/* Selected Cohort Details (placeholder for future modal) */}
      {selectedCohort && (
        <div className="cohort-analysis__selected-info">
          <p>
            Selected: {selectedCohort.cohortName} - {selectedCohort.period} - {selectedCohort.rate}%
          </p>
          <button
            onClick={() => setSelectedCohort(null)}
            className="cohort-analysis__close-button"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
};

CohortAnalysis.propTypes = {
  cohorts: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    size: PropTypes.number.isRequired,
    retention: PropTypes.shape({
      week1: PropTypes.number,
      week2: PropTypes.number,
      week4: PropTypes.number,
      month2: PropTypes.number,
      month3: PropTypes.number,
      month6: PropTypes.number
    }).isRequired
  })),
  averageRetention: PropTypes.shape({
    week1: PropTypes.number,
    week2: PropTypes.number,
    week4: PropTypes.number,
    month2: PropTypes.number,
    month3: PropTypes.number,
    month6: PropTypes.number
  }),
  bestCohort: PropTypes.shape({
    name: PropTypes.string,
    month3Retention: PropTypes.number
  }),
  worstCohort: PropTypes.shape({
    name: PropTypes.string,
    month3Retention: PropTypes.number
  }),
  churnWarnings: PropTypes.arrayOf(PropTypes.string),
  loading: PropTypes.bool,
  groupBy: PropTypes.oneOf(['week', 'month']),
  tier: PropTypes.oneOf(['all', 'free', 'premium', 'family']),
  onGroupByChange: PropTypes.func,
  onTierChange: PropTypes.func
};

export default CohortAnalysis;
